import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  MessageSquare,
  Eye,
  ArrowUpDown,
  CheckCircle,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TicketFilterBar } from "@sd/components/tickets/TicketFilterBar";
import { buildTicketDetailPath } from "@sd/lib/ticketDetailPath";
import {
  StatusChangeDialog,
  emptyStatusChange,
} from "@sd/components/tickets/StatusChangeDialog";
import { TicketStatusSelectItems } from "@sd/components/common/TicketStatusSelectItems";
import { ServiceDeskTicketSchemaDialog } from "@sd/components/tickets/ServiceDeskTicketSchemaDialog";
import {
  type StreamTicket,
  statusColors,
  priorityColors,
  normalizePriority,
  formatStatusLabel,
  formatSlaStatusLabel,
  formatTitleCase,
} from "@sd/lib/ticketStreamUtils";
import { getTicketQueueTitle } from "@sd/lib/ticketPageUtils";
import type { TicketStatus } from "@sd/types/ticket";
import { statusForSelect } from "@sd/constants/ticketStatuses";
import { assigneeMatchId } from "@sd/lib/adminTicketUtils";
import type { AdminTicket } from "@sd/types/admin";
import type { useTicketsPage } from "@sd/hooks/useTicketsPage";

const slaStatusIcons: Record<StreamTicket["slaStatus"], ReactNode> = {
  on_track: <CheckCircle className="h-4 w-4 text-green-500" />,
  at_risk: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
  breached: <XCircle className="h-4 w-4 text-red-500" />,
};

type TicketsVm = ReturnType<typeof useTicketsPage>;

export function TicketsStreamGrid({
  queue,
  vm,
  isAgentOnly,
}: {
  queue: string;
  vm: TicketsVm;
  isAgentOnly: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    search,
    setSearch,
    statusFilters,
    setStatusFilters,
    priorityFilters,
    setPriorityFilters,
    categoryFilters,
    setCategoryFilters,
    typeFilter,
    setTypeFilter,
    categories,
    filtered,
    queueFiltered,
    isPending,
    localRows,
    selectedIds,
    setSelectedIds,
    statusChangeDialog,
    setStatusChangeDialog,
    slaPriorityMap,
    assigneeSelectOptions,
    showQuickAddModal,
    setShowQuickAddModal,
    handleSort,
    handleStatusChange,
    confirmStatusChange,
    handleAssign,
    toggleSelect,
    toggleSelectAll,
    clearAllFilters,
    onTicketSchemaSaved,
    requesterFilters,
    setRequesterFilters,
    createdDateFilter,
    setCreatedDateFilter,
    requesterFilterOptions,
    priorityOptions,
  } = vm;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden p-4 lg:p-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-foreground">
            {getTicketQueueTitle(queue)}
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage and update tickets in this queue ({filtered.length} tickets)
          </p>
        </div>
        <Button
          className="w-full shrink-0 lg:w-auto"
          onClick={() => setShowQuickAddModal(true)}
        >
          <Plus className="mr-2 h-4 w-4" /> New
        </Button>
      </div>

      <TicketFilterBar
        search={search}
        onSearchChange={setSearch}
        statusFilters={statusFilters}
        onStatusFiltersChange={setStatusFilters}
        priorityFilters={priorityFilters}
        onPriorityFiltersChange={setPriorityFilters}
        priorityOptions={priorityOptions}
        categoryFilters={categoryFilters}
        onCategoryFiltersChange={setCategoryFilters}
        categories={categories}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        requesterFilters={requesterFilters}
        onRequesterFiltersChange={setRequesterFilters}
        requesterOptions={requesterFilterOptions}
        createdDateFilter={createdDateFilter}
        onCreatedDateFilterChange={setCreatedDateFilter}
        agentResponsive
        onReset={clearAllFilters}
        trailing={
          <Badge
            variant="outline"
            className="px-3 py-1.5 text-xs max-lg:w-full max-lg:justify-center"
          >
            {filtered.length} Tickets
          </Badge>
        }
      />

      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
          <span className="text-sm font-medium">
            {selectedIds.size} selected
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-auto rounded-lg border bg-card">
        {isPending && !localRows ? (
          <div className="flex flex-1 items-center justify-center p-12 text-sm text-muted-foreground">
            Loading tickets…
          </div>
        ) : filtered.length === 0 && queueFiltered.length > 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-12 text-center text-muted-foreground">
            <MessageSquare className="h-12 w-12 opacity-20" />
            <p className="text-sm">
              No tickets match the current filters in this queue.
            </p>
            <Button variant="outline" size="sm" onClick={clearAllFilters}>
              Clear all filters
            </Button>
          </div>
        ) : filtered.length === 0 &&
          queueFiltered.length === 0 &&
          queue !== "all" ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
            <MessageSquare className="h-12 w-12 opacity-20" />
            <p className="font-medium text-foreground">
              No tickets in this queue
            </p>
            <p className="max-w-md">
              Views like{" "}
              <code className="rounded bg-muted px-1">my-tickets</code>,{" "}
              <code className="rounded bg-muted px-1">vip</code>,{" "}
              <code className="rounded bg-muted px-1">unassigned</code> apply
              strict filters — try <strong>Stream (all)</strong> or the
              &quot;All&quot; queue in the sidebar.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-12 text-muted-foreground">
            <MessageSquare className="h-12 w-12 opacity-20" />
            <p className="text-sm">No tickets match your filters.</p>
          </div>
        ) : (
          <div className="max-lg:overflow-x-auto">
            <Table className="max-lg:min-w-[960px] lg:w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        selectedIds.size === filtered.length &&
                        filtered.length > 0
                      }
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("id")}
                  >
                    <span className="inline-flex items-center gap-1">
                      ID <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("subject")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Subject <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("priority")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Priority <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </TableHead>
                  <TableHead>SLA</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Change Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(ticket => (
                  <TableRow
                    key={ticket.id}
                    className={cn(selectedIds.has(ticket.id) && "bg-primary/5")}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(ticket.id)}
                        onCheckedChange={() => toggleSelect(ticket.id)}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm font-medium">
                      {ticket.uiId ?? ticket.id}
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <div className="truncate text-sm font-medium">
                        {ticket.subject}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1">
                        <span className="text-xs text-muted-foreground">
                          {formatTitleCase(ticket.category)} •{" "}
                          {formatTitleCase(ticket.channel)}
                        </span>
                        {ticket.tags.slice(0, 2).map(tag => (
                          <Badge
                            key={tag}
                            variant="outline"
                            className="h-4 px-1 text-[10px]"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{ticket.requester}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatTitleCase(ticket.department)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          "text-xs font-medium",
                          statusColors[ticket.status]
                        )}
                      >
                        {formatStatusLabel(ticket.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          "text-xs font-medium",
                          priorityColors[
                            normalizePriority(
                              ticket.priority !== "—"
                                ? ticket.priority
                                : ticket.priorityId
                                  ? (slaPriorityMap[ticket.priorityId] ?? "—")
                                  : "—"
                            )
                          ] ?? priorityColors.P3
                        )}
                      >
                        {ticket.priority !== "—"
                          ? ticket.priority
                          : ticket.priorityId
                            ? (slaPriorityMap[ticket.priorityId] ?? "—")
                            : "—"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {slaStatusIcons[ticket.slaStatus]}
                        <span className="text-xs">
                          {formatSlaStatusLabel(ticket.slaStatus)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={
                          assigneeMatchId(
                            {
                              assignedAgent: ticket.assignedAgent,
                            } as AdminTicket,
                            assigneeSelectOptions
                          ) ?? "__unassign__"
                        }
                        onValueChange={v => {
                          const agent = assigneeSelectOptions.find(
                            a => a.id === v
                          );
                          handleAssign(ticket.id, v, agent?.name ?? v);
                        }}
                      >
                        <SelectTrigger
                          className={`h-7 w-[140px] text-xs ${!ticket.assignedAgent ? "border-dashed" : ""}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unassign__">
                            Unassigned
                          </SelectItem>
                          {assigneeSelectOptions.map(a => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={statusForSelect(ticket.status)}
                        onValueChange={v =>
                          handleStatusChange(ticket, v as TicketStatus)
                        }
                      >
                        <SelectTrigger className="h-7 min-w-[160px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <TicketStatusSelectItems />
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          navigate(
                            buildTicketDetailPath(ticket.id, {
                              returnTo: `${location.pathname}${location.search}`,
                            })
                          )
                        }
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <StatusChangeDialog
        state={statusChangeDialog}
        onClose={() => setStatusChangeDialog(emptyStatusChange())}
        onConfirm={confirmStatusChange}
      />

      <ServiceDeskTicketSchemaDialog
        open={showQuickAddModal}
        onOpenChange={setShowQuickAddModal}
        onCreated={onTicketSchemaSaved}
      />
    </div>
  );
}
